import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { getCourses } from "@/api/getCourses";
import { createCourse } from "@/api/createCourse";
import { editCourse } from "@/api/editCourse";
import { deleteCourse } from "@/api/deleteCourse";
import { getCourseMetadata } from "@/api/getCourseMetadata";
import { getCourseDetail } from "@/api/getCourseDetail";
import { getModuleContentBlocks } from "@/api/getModuleContentBlocks";
import { saveModuleContentBlocks } from "@/api/saveModuleContentBlocks";
import type { Course, GetCoursesParams } from "@/api/getCourses";
import type { CreateCoursePayload } from "@/api/createCourse";
import type { EditCoursePayload } from "@/api/editCourse";
import type { CourseMetadata } from "@/api/getCourseMetadata";
import type { CourseDetail } from "@/types/learningCenter";
import type { ModuleContentBlock } from "@/modules/learningCenter/types/moduleContentBlock";

// State interface
export interface CourseState {
  courses: Course[];
  selectedCourse: Course | null;
  courseDetail: CourseDetail | null;
  metadata: CourseMetadata | null;
  moduleContentBlocks: Record<number, ModuleContentBlock[]>;
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
  filters: {
    tradeId?: number;
    skillId?: number;
    categoryId?: number;
    difficultyLevel?: "beginner" | "intermediate" | "advanced";
    hasQuiz?: boolean;
    search?: string;
    includeArchived?: boolean;
  };
  loading: {
    courses: boolean;
    courseDetail: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    metadata: boolean;
    contentBlocks: boolean;
    saveContentBlocks: boolean;
  };
  error: {
    courses: string | null;
    courseDetail: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
    metadata: string | null;
    contentBlocks: string | null;
    saveContentBlocks: string | null;
  };
}

// Initial state
const initialState: CourseState = {
  courses: [],
  selectedCourse: null,
  courseDetail: null,
  metadata: null,
  moduleContentBlocks: {},
  pagination: {
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {},
  loading: {
    courses: false,
    courseDetail: false,
    create: false,
    update: false,
    delete: false,
    metadata: false,
    contentBlocks: false,
    saveContentBlocks: false,
  },
  error: {
    courses: null,
    courseDetail: null,
    create: null,
    update: null,
    delete: null,
    metadata: null,
    contentBlocks: null,
    saveContentBlocks: null,
  },
};

// Async thunks
export const fetchCourses = createAsyncThunk(
  "course/fetchCourses",
  async (params: GetCoursesParams | undefined, { rejectWithValue }) => {
    try {
      const response = await getCourses(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses",
      );
    }
  },
);

export const createNewCourse = createAsyncThunk(
  "course/createCourse",
  async (payload: CreateCoursePayload, { rejectWithValue }) => {
    try {
      const response = await createCourse(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create course",
      );
    }
  },
);

export const updateCourse = createAsyncThunk(
  "course/updateCourse",
  async (
    { id, payload }: { id: number; payload: EditCoursePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await editCourse(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course",
      );
    }
  },
);

export const removeCourse = createAsyncThunk(
  "course/deleteCourse",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteCourse(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete course",
      );
    }
  },
);

export const fetchCourseMetadata = createAsyncThunk(
  "course/fetchMetadata",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCourseMetadata();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch metadata",
      );
    }
  },
);

export const fetchCourseDetail = createAsyncThunk(
  "course/fetchCourseDetail",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getCourseDetail(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course details",
      );
    }
  },
);

export const fetchModuleContentBlocks = createAsyncThunk(
  "course/fetchModuleContentBlocks",
  async (moduleId: number, { rejectWithValue }) => {
    try {
      const response = await getModuleContentBlocks(moduleId);
      return { moduleId, contentBlocks: response.contentBlocks };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch module content blocks",
      );
    }
  },
);

export const saveModuleContentBlocksThunk = createAsyncThunk(
  "course/saveModuleContentBlocks",
  async (
    {
      moduleId,
      contentBlocks,
    }: { moduleId: number; contentBlocks: ModuleContentBlock[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await saveModuleContentBlocks({
        moduleId,
        contentBlocks,
      });
      return { moduleId, contentBlocks: response.contentBlocks };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to save module content blocks",
      );
    }
  },
);

// Slice
const courseSlice = createSlice({
  name: "course",
  initialState,
  reducers: {
    setSelectedCourse: (state, action: PayloadAction<Course | null>) => {
      state.selectedCourse = action.payload;
    },
    setSelectedCourseDetail: (
      state,
      action: PayloadAction<CourseDetail | null>,
    ) => {
      state.courseDetail = action.payload;
    },
    clearCourseDetail: (state) => {
      state.courseDetail = null;
      state.loading.courseDetail = false;
      state.error.courseDetail = null;
    },
    setFilters: (state, action: PayloadAction<CourseState["filters"]>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    clearErrors: (state) => {
      state.error = {
        courses: null,
        courseDetail: null,
        create: null,
        update: null,
        delete: null,
        metadata: null,
        contentBlocks: null,
        saveContentBlocks: null,
      };
    },
    setModuleContentBlocks: (
      state,
      action: PayloadAction<{ moduleId: number; blocks: ModuleContentBlock[] }>,
    ) => {
      state.moduleContentBlocks[action.payload.moduleId] =
        action.payload.blocks;
    },
    clearModuleContentBlocks: (state, action: PayloadAction<number>) => {
      delete state.moduleContentBlocks[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Fetch courses
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.loading.courses = true;
        state.error.courses = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading.courses = false;
        state.courses = action.payload.courses;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading.courses = false;
        state.error.courses = action.payload as string;
      });

    // Create course
    builder
      .addCase(createNewCourse.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(createNewCourse.fulfilled, (state, action) => {
        state.loading.create = false;
        state.courses.unshift(action.payload as unknown as Course);
      })
      .addCase(createNewCourse.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload as string;
      });

    // Update course
    builder
      .addCase(updateCourse.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.courses.findIndex(
          (c) => c.id === action.payload.id,
        );
        if (index !== -1) {
          state.courses[index] = action.payload as unknown as Course;
        }
        if (state.selectedCourse?.id === action.payload.id) {
          state.selectedCourse = action.payload as unknown as Course;
        }
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload as string;
      });

    // Delete course (soft delete)
    builder
      .addCase(removeCourse.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(removeCourse.fulfilled, (state, action) => {
        state.loading.delete = false;
        // Remove from list (it's now archived)
        state.courses = state.courses.filter((c) => c.id !== action.payload);
        if (state.selectedCourse?.id === action.payload) {
          state.selectedCourse = null;
        }
      })
      .addCase(removeCourse.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload as string;
      });

    // Fetch metadata
    builder
      .addCase(fetchCourseMetadata.pending, (state) => {
        state.loading.metadata = true;
        state.error.metadata = null;
      })
      .addCase(fetchCourseMetadata.fulfilled, (state, action) => {
        state.loading.metadata = false;
        state.metadata = action.payload;
      })
      .addCase(fetchCourseMetadata.rejected, (state, action) => {
        state.loading.metadata = false;
        state.error.metadata = action.payload as string;
      });

    // Fetch course detail
    builder
      .addCase(fetchCourseDetail.pending, (state) => {
        state.loading.courseDetail = true;
        state.error.courseDetail = null;
      })
      .addCase(fetchCourseDetail.fulfilled, (state, action) => {
        state.loading.courseDetail = false;
        state.courseDetail = action.payload;
      })
      .addCase(fetchCourseDetail.rejected, (state, action) => {
        state.loading.courseDetail = false;
        state.error.courseDetail = action.payload as string;
      });

    // Fetch module content blocks
    builder
      .addCase(fetchModuleContentBlocks.pending, (state) => {
        state.loading.contentBlocks = true;
        state.error.contentBlocks = null;
      })
      .addCase(fetchModuleContentBlocks.fulfilled, (state, action) => {
        state.loading.contentBlocks = false;
        state.moduleContentBlocks[action.payload.moduleId] =
          action.payload.contentBlocks;
      })
      .addCase(fetchModuleContentBlocks.rejected, (state, action) => {
        state.loading.contentBlocks = false;
        state.error.contentBlocks = action.payload as string;
      });

    // Save module content blocks
    builder
      .addCase(saveModuleContentBlocksThunk.pending, (state) => {
        state.loading.saveContentBlocks = true;
        state.error.saveContentBlocks = null;
      })
      .addCase(saveModuleContentBlocksThunk.fulfilled, (state, action) => {
        state.loading.saveContentBlocks = false;
        state.moduleContentBlocks[action.payload.moduleId] =
          action.payload.contentBlocks;
      })
      .addCase(saveModuleContentBlocksThunk.rejected, (state, action) => {
        state.loading.saveContentBlocks = false;
        state.error.saveContentBlocks = action.payload as string;
      });
  },
});

// Actions
export const {
  setSelectedCourse,
  setSelectedCourseDetail,
  clearCourseDetail,
  setFilters,
  clearFilters,
  setPage,
  clearErrors,
  setModuleContentBlocks,
  clearModuleContentBlocks,
} = courseSlice.actions;

// Selectors
export const selectCourses = (state: RootState) => state.course.courses;
export const selectSelectedCourse = (state: RootState) =>
  state.course.selectedCourse;
export const selectCourseDetail = (state: RootState) =>
  state.course.courseDetail;
export const selectCourseDetailLoading = (state: RootState) =>
  state.course.loading.courseDetail;
export const selectCourseDetailError = (state: RootState) =>
  state.course.error.courseDetail;
export const selectCourseMetadata = (state: RootState) => state.course.metadata;
export const selectCoursePagination = (state: RootState) =>
  state.course.pagination;
export const selectCourseFilters = (state: RootState) => state.course.filters;
export const selectCourseLoading = (state: RootState) => state.course.loading;
export const selectCourseErrors = (state: RootState) => state.course.error;
export const selectModuleContentBlocks = (state: RootState, moduleId: number) =>
  state.course.moduleContentBlocks[moduleId] || [];
export const selectContentBlocksLoading = (state: RootState) =>
  state.course.loading.contentBlocks;
export const selectContentBlocksError = (state: RootState) =>
  state.course.error.contentBlocks;
export const selectSaveContentBlocksLoading = (state: RootState) =>
  state.course.loading.saveContentBlocks;
export const selectSaveContentBlocksError = (state: RootState) =>
  state.course.error.saveContentBlocks;

// Reducer
export default courseSlice.reducer;
